import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FaCheckCircle, FaWhatsapp, FaArrowLeft } from 'react-icons/fa';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { products as staticProducts } from '../data/products';

const ProductDetail = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = () => {
    const foundProduct = staticProducts.find(p => p.id === id);
    setProduct(foundProduct);
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Create WhatsApp message
    const message = `*Quote Request*%0A%0AProduct: ${product.name}%0AName: ${formData.name}%0AEmail: ${formData.email}%0APhone: ${formData.phone}%0AMessage: ${formData.message}`;
    
    // Redirect to WhatsApp
    window.open(`https://wa.me/919448650161?text=${message}`, '_blank');
    
    toast.success('Redirecting to WhatsApp...');
    setFormData({ name: '', email: '', phone: '', message: '' });
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center" data-testid="loading-state">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-4 border-[#2E7D32] border-t-transparent"></div>
          <p className="mt-4 text-[#4E5D50]">Loading product details...</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center" data-testid="not-found">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-[#1B3322] mb-4">Product Not Found</h2>
          <p className="text-[#4E5D50] mb-6">The product you're looking for doesn't exist.</p>
          <Link
            to="/products"
            className="inline-block bg-[#2E7D32] text-white px-8 py-3 rounded-full font-semibold hover:bg-[#1B5E20] transition-all"
          >
            Back to Products
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 bg-[#F1F8E9]" data-testid="product-detail-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back Button */}
        <Link
          to="/products"
          className="inline-flex items-center space-x-2 text-[#2E7D32] hover:text-[#1B5E20] mb-8 font-semibold"
          data-testid="back-button"
        >
          <FaArrowLeft />
          <span>Back to Products</span>
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          {/* Product Image */}
          <div>
            <img
              src={product.image_url}
              alt={product.name}
              className="w-full rounded-2xl shadow-lg"
              data-testid="product-image"
            />
          </div>

          {/* Product Info */}
          <div className="bg-white p-8 rounded-2xl shadow-lg">
            <div className="mb-4">
              <span className="inline-block bg-[#6ABD45] text-white px-4 py-2 rounded-full text-sm font-semibold">
                {product.category}
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold mb-4 text-[#2E368F]" data-testid="product-name">
              {product.name}
            </h1>
            <p className="text-lg text-gray-600 mb-8 leading-relaxed" data-testid="product-description">
              {product.description}
            </p>

            {/* Features */}
            <div className="mb-8">
              <h3 className="text-xl font-bold mb-4 text-[#2E368F]">Key Features</h3>
              <ul className="space-y-3">
                {product.features.map((feature, index) => (
                  <li key={index} className="flex items-start space-x-3" data-testid={`feature-${index}`}>
                    <FaCheckCircle className="text-[#6ABD45] mt-1 flex-shrink-0" />
                    <span className="text-gray-600">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href={`https://wa.me/919448650161?text=Hi, I'm interested in ${product.name}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 bg-[#25D366] text-white hover:bg-[#20BA5A] rounded-full px-6 py-4 font-semibold transition-all flex items-center justify-center gap-2"
                data-testid="whatsapp-inquiry-button"
              >
                <FaWhatsapp className="text-xl" />
                WhatsApp Inquiry
              </a>
              <button
                onClick={() => document.getElementById('quote-form').scrollIntoView({ behavior: 'smooth' })}
                className="flex-1 bg-[#6ABD45] text-white hover:bg-[#5AAC35] rounded-full px-6 py-4 font-semibold transition-all"
                data-testid="request-quote-button"
              >
                Request Quote
              </button>
            </div>
          </div>
        </div>

        {/* Specifications */}
        <div className="bg-white p-8 rounded-2xl shadow-lg mb-16">
          <h2 className="text-2xl font-bold mb-6 text-[#2E368F]">Technical Specifications</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {Object.entries(product.specifications).map(([key, value], index) => (
              <div key={index} className="border-b border-gray-200 pb-4" data-testid={`spec-${index}`}>
                <dt className="text-sm font-semibold text-gray-500 uppercase mb-1">{key}</dt>
                <dd className="text-lg text-[#2E368F]">{value}</dd>
              </div>
            ))}
          </div>
        </div>

        {/* Quote Form */}
        <div id="quote-form" className="bg-white p-8 rounded-2xl shadow-lg max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold mb-6 text-[#2E368F] text-center">Request a Quote</h2>
          <p className="text-gray-600 text-center mb-8">
            Fill out the form below and we'll connect with you on WhatsApp.
          </p>
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="quote-form">
            <div>
              <label className="block text-sm font-semibold text-[#2E368F] mb-2">Name *</label>
              <Input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Your full name"
                className="w-full"
                data-testid="quote-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#2E368F] mb-2">Email *</label>
              <Input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="your@email.com"
                className="w-full"
                data-testid="quote-email-input"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#2E368F] mb-2">Phone *</label>
              <Input
                type="tel"
                required
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+91 XXXXXXXXXX"
                className="w-full"
                data-testid="quote-phone-input"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#2E368F] mb-2">Message</label>
              <Textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder="Tell us about your requirements..."
                rows={4}
                className="w-full"
                data-testid="quote-message-input"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#6ABD45] hover:bg-[#5AAC35] text-white rounded-full py-6 text-lg font-semibold"
              data-testid="submit-quote-button"
            >
              Send via WhatsApp
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;